import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/data/forge";
import { MobileMenu } from "./MobileMenu";

export default function Nav() {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = location.pathname === "/";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || !onHome ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto w-[min(1180px,calc(100%-32px))] flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center"
          aria-label="The Forge home"
        >
          <img
            src={LOGO}
            alt="The Forge"
            className="h-16 w-16 rounded-2xl object-cover transition-transform duration-500 group-hover:rotate-12 group-hover:scale-105"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1 sm:gap-2">
          <Link
            to="/community"
            data-testid="nav-community"
            className="display text-[11px] tracking-[0.25em] px-3 py-2 text-white/60 hover:text-ember transition-colors"
          >
            Community
          </Link>
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="display text-[11px] tracking-[0.25em] px-3 py-2 text-forge hover:text-ember transition-colors"
            >
              Admin
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-1.5">
              <Link
                to={`/u/${user.user_id}`}
                data-testid="nav-profile"
                aria-label="Your profile"
                className="flex items-center gap-2 px-1.5 sm:px-3 py-1.5 rounded-full hover:bg-white/5 transition-colors"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    data-testid="nav-avatar"
                    className="w-8 h-8 rounded-full border border-white/20 object-cover"
                  />
                ) : (
                  <span
                    data-testid="nav-avatar"
                    className="w-8 h-8 rounded-full border border-forge/50 bg-forge/15 grid place-items-center display text-[11px] text-forge"
                  >
                    {(user.name || "F").slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="hidden md:inline display text-[11px] tracking-[0.2em] text-white/70 max-w-[140px] truncate">
                  {user.name}
                </span>
              </Link>
              <button
                data-testid="nav-logout"
                aria-label="Sign out"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="p-2 sm:px-3 rounded-full text-white/40 hover:text-forge transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 md:hidden" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M15 17l5-5-5-5M20 12H9M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="hidden md:inline display text-[11px] tracking-[0.2em]">Sign out</span>
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin"
              className="display text-[11px] tracking-[0.25em] px-4 py-2 rounded-full border border-white/15 text-white hover:border-ember hover:text-ember transition-colors"
            >
              Sign in
            </Link>
          )}
        </nav>

        <button
          data-testid="mobile-menu-button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          className="md:hidden p-2 -mr-2 text-white/70 hover:text-ember transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
        onLogout={async () => {
          await logout();
          navigate("/");
        }}
      />
    </header>
  );
}
